import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export const DEAFAULT_DELAY = 3500;
@Injectable({ providedIn: 'root' })
export class ToastService {
  toasts: any[] = [];

  constructor(private translate: TranslateService) {}

  show(title: string = '', body: string = '', options: any = {}) {
    if (options.shouldTranslate) {
      this.translate.get([`components.notification.${title}`, `components.notification.${body}`]).subscribe((translations: any) => {
        const translatedTitle = Object.values(translations)[0];
        const translatedBody = Object.values(translations)[1];
        this.toasts.push({
          title: translatedTitle,
          body: body.length ? translatedBody : '',
          ...options
        });
      });
    } else {
      this.toasts.push({ title, body, ...options });
    }
  }

  remove(toast: any) {
    this.toasts = this.toasts.filter((t) => t !== toast);
  }

  clear() {
    this.toasts.splice(0, this.toasts.length);
  }

  showSuccess(title: string = '', body: string = '', options: any = {}) {
    this.show(title, body, {
      classname: 'toast-success',
      delay: options.delay ? options.delay : DEAFAULT_DELAY,
      ...options
    });
  }

  showWarning(title: string = '', body: string = '', options: any = {}) {
    this.show(title, body, {
      classname: 'toast-warning',
      delay: options.delay ? options.delay : DEAFAULT_DELAY,
      ...options
    });
  }

  showDanger(title: string = '', body: string = '', options: any = {}) {
    this.show(title, body, {
      classname: 'toast-danger',
      delay: options.delay ? options.delay : DEAFAULT_DELAY,
      ...options
    });
  }

  showInfo(title: string = '', body: string = '', options: any = {}) {
    this.show(title, body, {
      classname: 'toast-info',
      delay: options.delay ? options.delay : DEAFAULT_DELAY,
      ...options
    });
  }

  showRequestError(err?: any) {
    const knownError = this.extractKnownError(err);
    if (knownError) {
      this.showDanger(knownError, '', { shouldTranslate: true });
    } else {
      this.showDanger('requestError', '', { shouldTranslate: true });
    }
  }

  showValidationOrRequestError(err: any) {
    const errors = err?.error?.errors;
    if (errors && typeof errors === 'object') {
      for (const fieldKey of Object.keys(errors)) {
        const camelKey = fieldKey.charAt(0).toLowerCase() + fieldKey.slice(1);
        const lookup = `components.notification.${camelKey}`;
        const translated = this.translate.instant(lookup);
        if (typeof translated === 'string' && translated !== lookup && translated.length > 0) {
          this.showDanger(translated, '', { shouldTranslate: false });
          return;
        }
      }
    }
    this.showRequestError(err);
  }

  private extractKnownError(err: any): string | null {
    const errors = err?.error?.errors;
    if (!errors) return null;
    for (const msgs of Object.values(errors)) {
      if (!Array.isArray(msgs)) continue;
      for (const msg of msgs) {
        if (msg === 'Such file already exists.') return 'duplicateFileError';
        // The server-side per-kind cap. Reported as "Sistēmas kļūda" until LNA-1850; the operator
        // needs to be told it is a limit, not a failure. The count is part of the server message,
        // so match on the stable prefix rather than the whole sentence.
        if (typeof msg === 'string' && msg.startsWith('At most ') && msg.includes('files can be attached here'))
          return 'serverMaxFilesError';
        if (msg === 'Deadline date must not be earlier than today.') return 'deadlineDateInPast';
        if (msg === 'Can not process logged additional information while backoffice and frontoffice status is not requested additional information')
          return 'statusAlreadyChanged';
        // LNA-2022. A backoffice request can only be submitted from Melnraksts, so this reaches the
        // operator only when the request moved on in another tab or by another user.
        if (msg === 'Can only submit a backoffice information request that is in Draft status.') return 'statusAlreadyChanged';
      }
    }
    return null;
  }

  showFormValidationError(errors: string[] = [], errorTranslationPrefix: string = '') {
    // Remove duplicates
    const uniqueErrors = [...new Set(errors)];

    const isSingle = uniqueErrors.length === 1;

    const title = isSingle
      ? `${this.translate.instant('components.notification.validationError')} ${this.translate.instant(`${errorTranslationPrefix}.${uniqueErrors[0]}`)}`
      : this.translate.instant('components.notification.validationErrors');

    const body = isSingle
      ? ''
      : `<ul>${uniqueErrors.map((error) => `<li>${this.translate.instant(`${errorTranslationPrefix}.${error}`)}</li>`).join('')}</ul>`;

    this.showDanger(title, body, { shouldTranslate: false });
  }
}
